import { isDrivable, latLngToLocalMeters, roadWidthMeters } from '@roadworld/map-generator';
import type { ChunkPayload, RoadClass } from '@roadworld/shared';
import type { ChunkCoord } from '../world/coords.js';

/**
 * Directed road graph for one chunk. Nodes are OSM nodes (positions in WORLD meters,
 * already offset by the chunk center), edges are directional segments of OSM ways.
 *
 * "Directed" because traffic flow follows OSM `oneway` semantics — a two-way road becomes
 * two edges (forward + reverse). A one-way road becomes one edge.
 *
 * NodeIds are OSM IDs, so a node at a chunk border appears in BOTH neighboring graphs with
 * the same id — that's the handoff key for cross-chunk path following.
 */
export interface RoadGraph {
  nodes: Map<number, GraphNode>;
  edges: GraphEdge[];
  chunkCoord: ChunkCoord;
}

export interface GraphNode {
  id: number;
  /** Position in WORLD meters (chunk center + local). */
  x: number;
  z: number;
  /** Out-edges (edge ids leaving this node). */
  outEdges: number[];
  /** Total incident edge count (in + out, double-counted for two-way). Used to flag
   *  intersections (degree >= 3) for the yield system in slice 5. */
  degree: number;
  isIntersection: boolean;
}

export interface GraphEdge {
  id: number;
  wayId: number;
  fromNode: number;
  toNode: number;
  /** Endpoints in WORLD meters. */
  x0: number;
  z0: number;
  x1: number;
  z1: number;
  /** Precomputed length (m). */
  length: number;
  /** Precomputed unit tangent. */
  tx: number;
  tz: number;
  roadClass: RoadClass;
  /** Half the road's width (m). */
  halfWidth: number;
  /** Is the underlying way one-way? */
  oneway: boolean;
}

/**
 * Build a RoadGraph for one chunk from its OSM payload.
 *
 * For each drivable way, emit forward edges along consecutive node pairs. If the way is
 * NOT oneway, also emit reverse edges so traffic can flow both directions.
 *
 * Nodes are deduped by OSM id — a node shared between multiple ways becomes one graph node
 * with many incident edges. Degree counts all incident edges (both directions); a node with
 * degree >= 3 is treated as an intersection.
 */
export function buildRoadGraph(
  payload: ChunkPayload,
  chunkCoord: ChunkCoord,
  chunkCenter: { x: number; z: number },
): RoadGraph {
  const nodes = new Map<number, GraphNode>();
  const edges: GraphEdge[] = [];

  const ensureNode = (id: number, lat: number, lng: number): GraphNode => {
    let n = nodes.get(id);
    if (n) return n;
    const lm = latLngToLocalMeters(lat, lng, payload.origin.lat, payload.origin.lng);
    n = {
      id,
      x: chunkCenter.x + lm.x,
      z: chunkCenter.z + lm.z,
      outEdges: [],
      degree: 0,
      isIntersection: false,
    };
    nodes.set(id, n);
    return n;
  };

  for (const way of payload.osm.ways) {
    if (!isDrivable(way.roadClass) || way.nodes.length < 2) continue;
    const halfWidth = roadWidthMeters(way.roadClass) * 0.5;

    let prev: GraphNode | null = null;
    for (const nodeId of way.nodes) {
      const osmNode = payload.osm.nodes[nodeId];
      if (!osmNode) {
        prev = null;
        continue;
      }
      const node = ensureNode(nodeId, osmNode.lat, osmNode.lng);
      if (prev) {
        const length = Math.hypot(node.x - prev.x, node.z - prev.z);
        if (length > 1e-3) {
          const tx = (node.x - prev.x) / length;
          const tz = (node.z - prev.z) / length;

          const fid = edges.length;
          edges.push({
            id: fid,
            wayId: way.id,
            fromNode: prev.id,
            toNode: node.id,
            x0: prev.x,
            z0: prev.z,
            x1: node.x,
            z1: node.z,
            length,
            tx,
            tz,
            roadClass: way.roadClass,
            halfWidth,
            oneway: way.oneway,
          });
          prev.outEdges.push(fid);
          prev.degree++;
          node.degree++;

          if (!way.oneway) {
            const rid = edges.length;
            edges.push({
              id: rid,
              wayId: way.id,
              fromNode: node.id,
              toNode: prev.id,
              x0: node.x,
              z0: node.z,
              x1: prev.x,
              z1: prev.z,
              length,
              tx: -tx,
              tz: -tz,
              roadClass: way.roadClass,
              halfWidth,
              oneway: false,
            });
            node.outEdges.push(rid);
            // Don't double-increment degree for the reverse direction — it's the same
            // physical road, just traversable both ways. degree counts physical incidences.
          }
        }
      }
      prev = node;
    }
  }

  // Mark intersections. Threshold = 3 incidences = at least Y/T-junction. A degree-2 node
  // is just a polyline knee on a straight stretch, no yield needed.
  for (const n of nodes.values()) {
    if (n.degree >= 3) n.isIntersection = true;
  }

  return { nodes, edges, chunkCoord };
}
